import { DateText, SkeletonList, isolate } from '@batiflow/ui';
import {
  Badge,
  Button,
  Checkbox,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  __css,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowUndo16Regular,
  Copy16Regular,
  DocumentAdd20Regular,
  LayoutColumnTwo20Regular,
} from '@fluentui/react-icons';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import {
  canEditDocument,
  canRestoreVersion,
  restoredFrom,
  shortHash,
  type DocumentDetail,
  type DocumentVersion,
} from './document-model';
import { useDocumentVersions } from './documents-api';
import { NewVersionDialog } from './new-version-dialog';
import { usePanelStyles } from './panel-styles';
import { RestoreDialog } from './restore-dialog';
import { useIssueMessage } from './upload-rows';
import type { Uploads } from './use-uploads';

const useStyles = __css({"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f1jat206","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"item":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","De3pzq":"f1dmdbja"},"head":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f1j0q4x9"},"index":{"Bhrd7zp":"fl43uef"},"reason":{"Btd35i7":"fjksvth"},"line":{"sj55zd":"fkfq4zb","Be2twd7":"fy9rknc","Btd35i7":"fjksvth"},"hash":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"f14mj54c","Be2twd7":"fy9rknc","sj55zd":"fkfq4zb"},"mono":{"Bahqtrf":"f1fedwem"},"rowActions":{"Cgygva":"ffriqlm"}});

/** The state of a version in words (a refused version also says why). */
function VersionStatus({ version }: { version: DocumentVersion }) {
  const { t } = useTranslation('documents');
  const issueMessage = useIssueMessage();
  const styles = useStyles();
  return (
    <>
      <Badge
        appearance="outline"
        color={
          version.status === 'rejected'
            ? 'danger'
            : version.status === 'available'
              ? 'success'
              : 'informative'
        }
      >
        {t(`status.${version.status}`)}
      </Badge>
      {version.status === 'rejected' ? (
        <span className={styles.line}>
          {issueMessage({ code: version.rejectionReason ?? 'unexpected' })}
        </span>
      ) : null}
    </>
  );
}

interface CopyHashProps {
  hash: string;
}

/** The first characters of the SHA-256, with a button that copies the whole value. */
function CopyHash({ hash }: CopyHashProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const notify = useNotify();
  const copy = () => {
    const done = () => {
      notify({ intent: 'success', message: t('versions.hashCopied') });
    };
    const failed = () => {
      notify({ intent: 'error', message: t('versions.hashCopyFailed') });
    };
    if (typeof navigator.clipboard === 'undefined') {
      failed();
      return;
    }
    navigator.clipboard.writeText(hash).then(done, failed);
  };
  return (
    <span className={styles.hash}>
      <span>{t('versions.hash')}</span>
      <bdi dir="ltr" className={styles.mono} title={hash}>
        {shortHash(hash)}…
      </bdi>
      <Button
        size="small"
        appearance="subtle"
        icon={<Copy16Regular />}
        aria-label={t('versions.copyHash', { hash })}
        onClick={copy}
      />
    </span>
  );
}

/** Choosing versions to compare (GED-03): the checkbox of one version, and how many are picked. */
interface ComparePick {
  picked: readonly string[];
  onToggle: (versionId: string, picked: boolean) => void;
}

interface VersionItemProps {
  version: DocumentVersion;
  versions: readonly DocumentVersion[];
  canRestore: boolean;
  onRestore: (version: DocumentVersion) => void;
  /** Absent when the viewer cannot be opened from here, or there is nothing to compare. */
  compare: ComparePick | undefined;
}

function VersionItem({ version, versions, canRestore, onRestore, compare }: VersionItemProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const source = restoredFrom(version, versions);
  const index = version.revisionIndex ? isolate(version.revisionIndex) : t('list.none');
  return (
    <li className={styles.item}>
      <div className={styles.head}>
        <span className={styles.index}>{t('versions.index', { index })}</span>
        {version.current ? (
          <Badge appearance="filled" color="brand">
            {t('versions.current')}
          </Badge>
        ) : null}
        <VersionStatus version={version} />
      </div>
      {version.revisionReason ? (
        <span className={styles.reason}>
          {t('versions.reasonLabel')} <bdi>{version.revisionReason}</bdi>
        </span>
      ) : null}
      {version.restoredFromVersionId ? (
        <span className={styles.line}>
          {source?.revisionIndex
            ? t('versions.restoredFrom', { index: isolate(source.revisionIndex) })
            : t('versions.restoredFromOlder')}
        </span>
      ) : null}
      <span className={styles.line}>
        {t('versions.by', {
          author: isolate(version.uploadedByName ?? t('versions.unknownAuthor')),
          organization: isolate(version.ownerOrgName ?? t('list.partner')),
        })}{' '}
        <DateText value={version.createdAt} variant="dateTime" />
      </span>
      {version.code ? (
        <span className={styles.line}>
          {t('versions.code')} <bdi dir="ltr">{version.code}</bdi>
        </span>
      ) : null}
      {version.sha256 ? <CopyHash hash={version.sha256} /> : null}
      {compare && version.status === 'available' ? (
        <Checkbox
          size="medium"
          label={t('versions.compareSelect', { index })}
          checked={compare.picked.includes(version.id)}
          disabled={compare.picked.length >= 2 && !compare.picked.includes(version.id)}
          onChange={(_event, data) => {
            compare.onToggle(version.id, data.checked === true);
          }}
        />
      ) : null}
      {canRestore && canRestoreVersion(version) ? (
        <div className={styles.rowActions}>
          <Button
            size="small"
            icon={<ArrowUndo16Regular />}
            aria-label={t('versions.restoreLabel', { index })}
            onClick={() => {
              onRestore(version);
            }}
          >
            {t('versions.restore')}
          </Button>
        </div>
      ) : null}
    </li>
  );
}

export interface VersionHistoryProps {
  spaceId: string;
  document: DocumentDetail;
  uploads: Uploads;
  /** Where the viewer opens, for "Compare"; null when the reader cannot open it from here. */
  viewer?: { orgId: string; projectId: string } | null;
}

/**
 * The versions of a document, newest first: index, reason, author and organisation, date, SHA-256,
 * state in words, "current" marker and "restored from" note. People who can edit (and a document
 * no approval holds) can restore an available version or add a new one.
 */
export function VersionHistory({ spaceId, document, uploads, viewer }: VersionHistoryProps) {
  const { t } = useTranslation('documents');
  const styles = usePanelStyles();
  const navigate = useNavigate();
  const [picked, setPicked] = useState<readonly string[]>([]);
  const versions = useDocumentVersions(spaceId, document.id);
  const [restoring, setRestoring] = useState<DocumentVersion | null>(null);
  const [adding, setAdding] = useState(false);

  const mayChange = canEditDocument(document) && document.lockedByFlowId === null;
  const items = versions.data?.items ?? [];
  // Two readable versions are needed to compare; the choice lives here, the comparison in the viewer.
  const readable = items.filter((version) => version.status === 'available');
  const compare: ComparePick | undefined =
    viewer && readable.length >= 2
      ? {
          picked,
          onToggle: (versionId, on) => {
            setPicked((previous) =>
              on
                ? previous.includes(versionId) || previous.length >= 2
                  ? previous
                  : [...previous, versionId]
                : previous.filter((id) => id !== versionId),
            );
          },
        }
      : undefined;
  const [firstPicked, secondPicked] = picked;
  const compareNow = () => {
    if (!viewer || !firstPicked || !secondPicked) return;
    void navigate({
      to: '/o/$orgId/projects/$projectId/documents/$documentId',
      params: { orgId: viewer.orgId, projectId: viewer.projectId, documentId: document.id },
      search: { versionId: firstPicked, compare: secondPicked },
    });
  };

  return (
    <section className={styles.section} aria-label={t('versions.region')}>
      <div className={styles.sectionHead}>
        <h3 className={styles.heading}>{t('versions.title')}</h3>
        {mayChange ? (
          <Button
            size="small"
            icon={<DocumentAdd20Regular />}
            onClick={() => {
              setAdding(true);
            }}
          >
            {t('versions.newVersion')}
          </Button>
        ) : null}
      </div>
      {versions.isError ? (
        <MessageBar intent="error" layout="multiline">
          <MessageBarBody>{t('versions.loadFailed')}</MessageBarBody>
          <MessageBarActions>
            <Button
              onClick={() => {
                void versions.refetch();
              }}
            >
              {t('common:actions.retry')}
            </Button>
          </MessageBarActions>
        </MessageBar>
      ) : !versions.data ? (
        <SkeletonList count={3} />
      ) : items.length === 0 ? (
        <p className={styles.muted}>{t('versions.none')}</p>
      ) : (
        <>
          {compare ? (
            <div className={styles.actions}>
              <Button
                size="small"
                icon={<LayoutColumnTwo20Regular />}
                disabled={picked.length !== 2}
                onClick={compareNow}
              >
                {t('versions.compareAction')}
              </Button>
              <span className={styles.muted} role="status">
                {t(
                  picked.length === 0
                    ? 'versions.compareHintNone'
                    : picked.length === 1
                      ? 'versions.compareHintOne'
                      : 'versions.compareHintReady',
                )}
              </span>
            </div>
          ) : null}
          <VersionList
            items={items}
            canRestore={mayChange}
            onRestore={setRestoring}
            label={t('versions.listLabel')}
            compare={compare}
          />
        </>
      )}

      {restoring ? (
        <RestoreDialog
          spaceId={spaceId}
          documentId={document.id}
          version={restoring}
          onClose={() => {
            setRestoring(null);
          }}
        />
      ) : null}
      {adding ? (
        <NewVersionDialog
          document={document}
          uploads={uploads}
          onClose={() => {
            setAdding(false);
          }}
        />
      ) : null}
    </section>
  );
}

interface VersionListProps {
  items: readonly DocumentVersion[];
  canRestore: boolean;
  onRestore: (version: DocumentVersion) => void;
  label: string;
  compare: ComparePick | undefined;
}

function VersionList({ items, canRestore, onRestore, label, compare }: VersionListProps) {
  const styles = useStyles();
  return (
    <ul className={styles.list} aria-label={label}>
      {items.map((version) => (
        <VersionItem
          key={version.id}
          version={version}
          versions={items}
          canRestore={canRestore}
          onRestore={onRestore}
          compare={compare}
        />
      ))}
    </ul>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/version-history.griffel.css";